
import { useOrderDataLoader } from './useOrderDataLoader';
import { useOrderSubmit } from './useOrderSubmit';
import { useProductManagement } from '../useProductManagement';
import { useCustomerSelection } from './useCustomerSelection';
import { OrderWithCustomer } from '../../types/orderTypes';

interface UseOrderEditProps {
  orderId: string;
  handleOrderUpdated: (updatedOrder: OrderWithCustomer) => void;
  handleBackToOrders: () => void;
  organizationId?: string | null;
}

export const useOrderEdit = ({ 
  orderId, 
  handleOrderUpdated, 
  handleBackToOrders,
  organizationId
}: UseOrderEditProps) => {
  // Load order data
  const {
    isLoading,
    customerId,
    customerName,
    customerPhone,
    customerEmail,
    customerCountry,
    customerRegion,
    customerMunicipality,
    selectedCustomer,
    orderNumber,
    orderDate,
    shippingAddress,
    notes,
    products,
    totalAmount,
    setCustomerId,
    setCustomerName,
    setCustomerPhone,
    setCustomerEmail,
    setCustomerCountry,
    setCustomerRegion,
    setCustomerMunicipality,
    setSelectedCustomer,
    setOrderNumber,
    setOrderDate,
    setShippingAddress,
    setNotes,
    setProducts,
    setTotalAmount
  } = useOrderDataLoader(orderId, handleBackToOrders, organizationId);

  // Handle product operations
  const {
    handleAddProduct, 
    handleRemoveProduct
  } = useProductManagement(products, setProducts, setTotalAmount);

  // Handle customer selection - passing as a single object rather than individual parameters
  const {
    handleSelectCustomer
  } = useCustomerSelection({
    setCustomerId, 
    setCustomerName, 
    setCustomerPhone, 
    setSelectedCustomer, 
    setShippingAddress,
    setCustomerEmail,
    setCustomerCountry,
    setCustomerRegion,
    setCustomerMunicipality
  });

  // Handle form submission
  const {
    isSubmitting,
    handleSubmit
  } = useOrderSubmit({
    orderId,
    customerId,
    customerName,
    products,
    orderNumber,
    orderDate,
    shippingAddress,
    notes,
    handleOrderUpdated,
    handleBackToOrders,
    customerCountry,
    customerRegion,
    customerMunicipality
  });

  return {
    customerId,
    customerName,
    customerPhone,
    customerEmail,
    customerCountry,
    customerRegion,
    customerMunicipality,
    selectedCustomer,
    orderNumber,
    orderDate,
    shippingAddress,
    notes,
    products,
    totalAmount,
    isSubmitting,
    isLoading,
    setOrderNumber,
    setOrderDate,
    setShippingAddress,
    setNotes,
    setCustomerCountry,
    setCustomerRegion,
    setCustomerMunicipality,
    handleSelectCustomer,
    handleAddProduct,
    handleRemoveProduct,
    handleSubmit,
  };
};
